import React, { useState, useRef, useEffect } from 'react';
import { Box, HStack, Center } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { ReCAPTCHA } from 'react-google-recaptcha';
import { isEmpty } from 'lodash-es';
import { getCountryCallingCode } from 'libphonenumber-js/max';
import { useFormContext, useController } from 'react-hook-form';
import { RECAPTCHA_TOKEN_FIELD } from '../../../../../constants/recaptchable';
import authenticationService from '../../../../../service/authenticationService';
import {
  PINCODE_COUNT_DOWN_TIME,
  PINCODE_LENGTH,
} from '../../../constants/pincode';
import extractServerErrorMessages from '../../../utils/extractServerErrorMessages';
import { useAppContext } from '../../../context/appContext';
import { useCountdownTimer } from '../../../hooks';
import { Input, Button, TitleText, ErrorBanner } from '../../../components';

// eslint-disable-next-line react/prop-types
const StepTwo = ({ handleNextStep, handlePrevStep }) => {
  const { t } = useTranslation();
  const { grecaptcha, ensuredRecaptchaSiteKey } = useAppContext();
  const {
    errors,
    control,
    handleSubmit,
    setError,
    getValues,
  } = useFormContext();
  const { field: pincodeField } = useController({
    name: 'pincode',
    control,
    defaultValue: '',
  });

  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [hasSentCode, setHasSentCode] = useState(false);
  const [sendCodeErrors, setSendCodeErrors] = useState([]);
  const { timeLeft, hasFinished, start: startTimer } = useCountdownTimer({
    defaultSeconds: PINCODE_COUNT_DOWN_TIME,
  });
  const refReCAPTCHA = useRef();

  const handleSendCode = async () => {
    try {
      const token = await refReCAPTCHA.current?.executeAsync();
      refReCAPTCHA.current?.reset();
      setIsSendingCode(true);
      setSendCodeErrors([]);
      startTimer();
      const payload = {
        country_calling_code: getCountryCallingCode(getValues('phone_country')),
        mobile_phone: getValues('mobile_phone'),
        is_forget_password: false,
        [RECAPTCHA_TOKEN_FIELD]: token,
      };
      await authenticationService.sendCode(payload);
      setHasSentCode(true);
    } catch (e) {
      const data = e?.response?.data ?? {};
      setSendCodeErrors(extractServerErrorMessages(data));
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerify = async () => {
    try {
      setIsVerifyingCode(true);
      const payload = {
        country_calling_code: getCountryCallingCode(getValues('phone_country')),
        mobile_phone: getValues('mobile_phone'),
        code: pincodeField.value,
      };
      await authenticationService.verifyCode(payload);
      handleNextStep();
    } catch (error) {
      setError('pincode', {
        type: 'custom',
        message: error?.response?.data?.error_messages.join(),
      });
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const onSubmit = () => {
    if (!hasSentCode) {
      return;
    }
    handleVerify();
  };

  useEffect(() => {
    if (!hasSentCode || !isEmpty(errors?.pincode?.message)) {
      return;
    }
    if (pincodeField.value.length === PINCODE_LENGTH) {
      onSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pincodeField.value, hasSentCode]);

  return (
    <>
      <Box>
        <TitleText mb="32px">{t('Verify mobile number')}</TitleText>
        <ErrorBanner errorMessages={sendCodeErrors} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <HStack gap="12px" alignItems="flex-end">
            <Box flex="1">
              <Input
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                maxLength={PINCODE_LENGTH}
                pattern={`\\d{${PINCODE_LENGTH}}`}
                placeholder={t('Enter verification code')}
                label={t('Verification code')}
                value={pincodeField.value}
                onChange={(e) => pincodeField.onChange(e.target.value)}
                isInvalid={!isEmpty(errors?.pincode?.message)}
                errorMessage={
                  errors?.pincode?.message?.translateKey
                    ? t(
                        errors?.pincode?.message?.translateKey,
                        errors?.pincode?.message?.params,
                      )
                    : errors?.pincode?.message
                }
              />
            </Box>
            <Button
              onClick={handleSendCode}
              variant="outlineSendCode"
              size="sm"
              minWidth="80px"
              mb={
                !isEmpty(errors?.pincode?.message) ? '15px !important' : '0px'
              }
              isLoading={isSendingCode}
              isDisabled={hasSentCode && !hasFinished}
            >
              {hasSentCode && !hasFinished
                ? t('Seconds left', { seconds: timeLeft })
                : t('Send verification code')}
            </Button>
          </HStack>
          <ReCAPTCHA
            ref={refReCAPTCHA}
            size="invisible"
            grecaptcha={grecaptcha}
            sitekey={ensuredRecaptchaSiteKey}
          />
          <Button
            isFullWidth
            variant="solid"
            mt="32px"
            onClick={handleSubmit(onSubmit)}
            isDisabled={!hasSentCode || !isEmpty(errors?.pincode?.message)}
            isLoading={isVerifyingCode}
          >
            {t('Next')}
          </Button>
        </form>
        <Center mt="64px">
          <Button onClick={handlePrevStep} variant="link" size="sm">
            {t('Back')}
          </Button>
        </Center>
      </Box>
    </>
  );
};

export default StepTwo;
